import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import { Selector } from '@/components/ui/inputs/selector';
import Table from '@/components/ui/tables/Table';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { HolidayByRegionItem } from '@/api/domains/attendance';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';


interface HolidayByRegionFilters {
  year: number | null;
  month: number | null;
  regionId: number | null;
  holidayTypeId: number | null;
}

interface HolidayByRegionTabProps {
  rows: HolidayByRegionItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<HolidayByRegionItem>[];
  actions: TableAction<HolidayByRegionItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  openCreatePanel: () => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
  filters: HolidayByRegionFilters;
  setFilters: (filters: HolidayByRegionFilters) => void;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  regionOptions: { value: string; label: string }[];
  holidayTypeOptions: { value: string; label: string }[];
}

export const HolidayByRegionTab: React.FC<HolidayByRegionTabProps> = ({
  rows,
  total,
  page,
  pageSize,
  setPage,
  setPageSize,
  columns,
  actions,
  onSearch,
  onSortChange,
  loading,
  openCreatePanel,
  onExport,
  isExporting,
  filters,
  setFilters,
  yearOptions,
  monthOptions,
  regionOptions,
  holidayTypeOptions,
}) => {
  const { t: translate } = useTranslation();

  return (
    <div data-testid="holiday-by-region-tab">
      <div className="mb-4">
        <PermissionGuard action="create">
          <PrimaryButton
            iconLeft={<IconScout name="plus" />}
            onClick={openCreatePanel}
            data-testid="create-holiday-by-region-button"
          >
            {translate('attendance:holiday.actions.createByRegion')}
          </PrimaryButton>
        </PermissionGuard>
      </div>

      <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
        <FilterDropdown
          extraFilters={
            <div className="flex flex-col gap-3 pt-2">
              <Selector
                label={translate('attendance:holiday.filters.year')}
                options={yearOptions}
                value={filters.year !== null ? String(filters.year) : ''}
                onChange={(value: string | string[]) =>
                  setFilters({ ...filters, year: normalizeNumericSelectValue(value) ?? null })
                }
                data-testid="holiday-by-region-year-filter"
              />
              <Selector
                label={translate('attendance:holiday.filters.month')}
                options={monthOptions}
                value={filters.month !== null ? String(filters.month) : ''}
                onChange={(value: string | string[]) =>
                  setFilters({ ...filters, month: normalizeNumericSelectValue(value) ?? null })
                }
                data-testid="holiday-by-region-month-filter"
              />
              <Selector
                label={translate('attendance:holiday.filters.region')}
                options={regionOptions}
                value={filters.regionId !== null ? String(filters.regionId) : ''}
                onChange={(value: string | string[]) =>
                  setFilters({ ...filters, regionId: normalizeNumericSelectValue(value) ?? null })
                }
                data-testid="holiday-by-region-region-filter"
              />
              <Selector
                label={translate('attendance:holiday.filters.holidayType')}
                options={holidayTypeOptions}
                value={filters.holidayTypeId !== null ? String(filters.holidayTypeId) : ''}
                onChange={(value: string | string[]) => {
                  const holidayTypeId = normalizeNumericSelectValue(value) ?? null;
                  setFilters({ ...filters, holidayTypeId });
                }}
                data-testid="holiday-by-region-type-filter"
              />
            </div>
          }
        />
        <ExportDropdown
          onExport={onExport}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        searchable
        onSearch={onSearch}
        onSortChange={onSortChange}
        serverSideSort
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        isLoading={loading}
        actions={actions}
        data-testid="holiday-by-region-table"
      />
    </div>
  );
};

export default HolidayByRegionTab;
